import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./css/BlogsCommon.css";
import "./css/Blogs.css";
import blogsAPI from "../services/BlogsAPI";
import { formatDate } from "../utils/DateUtil";
import LoadingSpinner from "../common/LoadingSpinner";

const Blogs = () => {
  const { shortTitle } = useParams(); // Changed from blogId to shortTitle
  const navigate = useNavigate();
  const [currentBlog, setCurrentBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareNotification, setShareNotification] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Show share notification
  const showShareNotification = (message, type = "success") => {
    setShareNotification({ message, type });
    setTimeout(() => {
      setShareNotification(null);
    }, 3000);
  };

  // Function to fetch specific blog by short title
  const fetchBlogByShortTitle = async (shortTitle) => {
    if (!shortTitle || shortTitle.trim().length === 0) {
      setError("Invalid blog short title provided");
      setIsLoading(false);
      return;
    }

    console.log(`🔄 Fetching blog with short title: ${shortTitle}`);
    setIsLoading(true);
    setError(null);

    try {
      const blog = await blogsAPI.getBlogByShortTitle(shortTitle);
      console.log(`✅ Successfully loaded blog: "${blog.title}"`);

      setCurrentBlog(blog);
    } catch (error) {
      console.error(`❌ Error fetching blog ${shortTitle}:`, error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to fetch blog when component mounts or shortTitle changes
  useEffect(() => {
    console.log(`🔍 Blogs.js: shortTitle from URL params: "${shortTitle}"`);
    if (shortTitle) {
      fetchBlogByShortTitle(shortTitle);
    } else {
      console.log("❌ Blogs.js: No shortTitle found in URL params");
      setError("No blog short title provided");
      setIsLoading(false);
    }
  }, [shortTitle]);

  // Effect to handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleBackToHome = () => {
    console.log("📰 Navigating back to blogs section");
    // Navigate to the main page with blogs section anchor
    navigate("/#blogs");
    // Alternative: if the blogs section is at a specific route, use:
    // navigate("/");
    // Then scroll to blogs section after navigation
    setTimeout(() => {
      const blogsSection = document.querySelector(
        '#blogs, [data-section="blogs"], .landing-page-blogs',
      );
      if (blogsSection) {
        blogsSection.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleBlogSelect = (blogShortTitle) => {
    console.log("📰 User selected blog:", blogShortTitle);
    navigate(`/blogs/${blogShortTitle}`);
  };

  // Sharing functionality
  const getCurrentBlogUrl = () => {
    return `${window.location.origin}/blogs/${shortTitle}`;
  };

  const getCurrentBlogTitle = () => {
    return currentBlog?.title || "Check out this blog post";
  };

  const getCurrentBlogSummary = () => {
    return currentBlog?.summary || "An interesting article from KinShield";
  };

  const handleEmailShare = () => {
    const blogUrl = getCurrentBlogUrl();
    const blogTitle = getCurrentBlogTitle();
    const blogSummary = getCurrentBlogSummary();
    const blogCategory = currentBlog?.category || "Digital Safety";
    const blogAuthor = currentBlog?.author || "KinShield Team";

    const publishedDate = formatDate(currentBlog?.published_date);

    const rawSummary = getCurrentBlogSummary();
    const summary = rawSummary
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const maxSummaryLen = 500;
    const trimmedSummary =
      summary.length > maxSummaryLen
        ? summary.slice(0, maxSummaryLen - 3) + "..."
        : summary;

    const subject = `KinShield | ${blogCategory}: ${blogTitle}`;
    const bodyLines = [
      `I thought you'd appreciate this ${blogCategory.toLowerCase()} article:`,
      ``,
      `Title: ${blogTitle}`,
      `Author: ${blogAuthor}`,
      `Published: ${publishedDate}`,
      ``,
      `Summary: ${trimmedSummary}`,
      ``,
      `Read more: ${blogUrl}`,
      ``,
      `Stay safe online,`,
      `KinShield Team`,
    ];
    let body = bodyLines.join("\n");

    const encoded = (s) => encodeURIComponent(s);
    let mailtoHref = `mailto:?subject=${encoded(subject)}&body=${encoded(
      body,
    )}`;
    if (mailtoHref.length > 1900) {
      body = `Read this article: ${blogTitle}\n${blogUrl}`;
      mailtoHref = `mailto:?subject=${encoded(subject)}&body=${encoded(body)}`;
    }

    try {
      window.location.href = mailtoHref;
      showShareNotification("Opening email client...");
    } catch (err) {
      try {
        const a = document.createElement("a");
        a.href = mailtoHref;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showShareNotification("Opening email client...");
      } catch (inner) {
        navigator.clipboard
          ?.writeText(`${subject}\n\n${body}`)
          .then(() => {
            showShareNotification(
              "Couldn't open email app. Content copied.",
              "info",
            );
          })
          .catch(() => {
            showShareNotification("Email share failed", "error");
          });
      }
    }
  };

  const handleSocialShare = (platform) => {
    const blogUrl = getCurrentBlogUrl();
    const blogTitle = getCurrentBlogTitle();
    const blogSummary = getCurrentBlogSummary();
    const blogCategory = currentBlog?.category || "Digital Safety";
    const blogAuthor = currentBlog?.author || "KinShield Team";

    // Helper function to truncate text for platform limits
    const truncateText = (text, maxLength) => {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength - 3) + "...";
    };

    // Generate meaningful, engaging content for each platform
    let shareUrl = "";
    let message = "";

    switch (platform) {
      case "x":
      case "twitter":
        // X/Twitter has 280 character limit, account for URL (~23 chars) and hashtags
        const twitterHashtags =
          "#CyberSecurity #OnlineSafety #DigitalLiteracy #KinShield";
        const maxTwitterText = 280 - twitterHashtags.length - 25; // Leave room for URL and hashtags

        message = `�️ Essential ${blogCategory} Knowledge!\n\n📖 "${truncateText(
          blogTitle,
          80,
        )}"\n\n💡 ${truncateText(
          blogSummary,
          maxTwitterText - 100,
        )}\n\n👆 Read the full guide to stay protected online!\n\n${twitterHashtags}`;

        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          message,
        )}&url=${encodeURIComponent(blogUrl)}`;
        break;

      case "facebook":
        // Facebook allows longer posts - create rich, engaging content
        message = `🔒 IMPORTANT SECURITY ALERT 🔒\n\n📰 New Article: "${blogTitle}"\n\n🎯 Category: ${blogCategory}\n✍️ By: ${blogAuthor}\n\n📝 What you'll learn:\n${blogSummary}\n\n🛡️ Why this matters:\nIn today's digital world, staying informed about cybersecurity threats is crucial for protecting yourself and your family. This comprehensive guide provides practical, actionable advice you can implement immediately.\n\n👥 Share this with friends and family who might benefit from these security tips!\n\n🔗 Read the full article here: ${blogUrl}\n\n#CyberSecurity #OnlineSafety #DigitalProtection #FamilySafety #KinShield`;

        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          blogUrl,
        )}&quote=${encodeURIComponent(message)}`;
        break;

      case "linkedin":
        // LinkedIn professional tone with industry focus
        message = `🔐 Professional Development in Cybersecurity\n\n📊 Industry Insight: "${blogTitle}"\n\n🎯 Focus Area: ${blogCategory}\n👨‍💼 Author: ${blogAuthor}\n\n� Key Takeaways:\n${blogSummary}\n\n💼 Why this matters for professionals:\nCybersecurity awareness is no longer optional—it's a critical business skill. Whether you're protecting corporate data, client information, or personal assets, understanding these security principles can prevent costly breaches and protect your professional reputation.\n\n🚀 This knowledge is particularly valuable for:\n• IT professionals and developers\n• Business executives and managers\n• Remote workers and consultants\n• Anyone handling sensitive data\n\n📖 Dive deeper into this topic: ${blogUrl}\n\n#CyberSecurity #ProfessionalDevelopment #BusinessSecurity #DigitalTransformation #InfoSec #KinShield`;

        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          blogUrl,
        )}`;

        // For LinkedIn, we'll copy the message to clipboard since their sharing API is limited
        navigator.clipboard
          .writeText(message)
          .then(() => {
            console.log("LinkedIn message copied to clipboard");
            showShareNotification(
              "LinkedIn post content copied to clipboard! Paste it when sharing.",
              "info",
            );
          })
          .catch(() => {
            console.log("Failed to copy to clipboard");
          });
        break;

      default:
        return;
    }

    // Open share window
    window.open(
      shareUrl,
      "_blank",
      "width=600,height=500,scrollbars=yes,resizable=yes",
    );

    // Show success notification
    if (platform !== "linkedin") {
      showShareNotification(
        `Opening ${
          platform === "x"
            ? "X (Twitter)"
            : platform.charAt(0).toUpperCase() + platform.slice(1)
        } share dialog...`,
      );
    }
  };

  const handleTextShare = () => {
    const blogUrl = getCurrentBlogUrl();
    const blogTitle = getCurrentBlogTitle();
    const blogSummary = getCurrentBlogSummary();
    const blogCategory = currentBlog?.category || "Digital Safety";

    // Create engaging SMS content with emojis and clear formatting
    const textMessage = `🛡️ ${blogCategory} Alert!\n\n📰 "${blogTitle}"\n\n💡 Quick summary:\n${blogSummary.substring(
      0,
      120,
    )}${
      blogSummary.length > 120 ? "..." : ""
    }\n\n🔗 Read more: ${blogUrl}\n\n📱 From KinShield - stay safe online!`;

    // Create SMS link (works on mobile devices)
    const smsLink = `sms:?&body=${encodeURIComponent(textMessage)}`;

    // Try to open SMS app, fallback to copying text
    try {
      window.location.href = smsLink;
      showShareNotification("Opening messaging app with pre-filled text...");
    } catch (error) {
      // Fallback: copy to clipboard
      handleCopyTextMessage(textMessage);
      showShareNotification("Text message copied to clipboard!", "info");
    }
  };

  const handleCopyTextMessage = async (message) => {
    try {
      await navigator.clipboard.writeText(message);
      console.log("✅ Text message copied to clipboard");
    } catch (error) {
      console.error("❌ Failed to copy text message:", error);
    }
  };

  const handleCopyLink = async () => {
    const blogUrl = getCurrentBlogUrl();
    try {
      await navigator.clipboard.writeText(blogUrl);
      setLinkCopied(true);
      console.log("✅ Blog link copied to clipboard");

      // Reset the icon back to copy after 2 seconds
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    } catch (error) {
      console.error("❌ Failed to copy link:", error);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = blogUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      setLinkCopied(true);
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    }
  };

  return (
    <div className="blogs-container">
      {/* Share Notification */}
      {shareNotification && (
        <div className={`share-notification ${shareNotification.type}`}>
          <span>{shareNotification.message}</span>
          <button
            onClick={() => setShareNotification(null)}
            className="notification-close"
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      )}

      {/* Back Button */}
      <div className="back-button-wrapper">
        <button onClick={handleBackToHome} className="back-button">
          ← Back to Blog Section
        </button>
      </div>

      {/* Header Section */}
      <div className="blogs-header">
        <h1 className="page-title">
          KinShield<span className="trademark">™</span> Blogs
        </h1>
      </div>

      {/* Main Content */}
      <div className="blog-content-wrapper">
        {isLoading ? (
          <div className="loading-state">
            <LoadingSpinner message="Loading blog post..." />
          </div>
        ) : error ? (
          // Error State
          <div className="error-state">
            <h2>Oops! Something went wrong</h2>
            <p>{error}</p>
            <button onClick={handleBackToHome} className="back-button">
              ← Back to Blog Section
            </button>
          </div>
        ) : currentBlog ? (
          // Blog Content
          <>
            {/* Blog Content 
                Renders HTML content directly from the backend without sanitization.
                This is safe because:
                1. Blog content is created/managed by trusted admin users only
                2. Content is not user-generated or from untrusted sources
                3. All inline styles, colors, and formatting are preserved
                
                Note: dangerouslySetInnerHTML is React's way of setting innerHTML.
                The "dangerous" name is a reminder that unfiltered HTML could contain
                malicious scripts if the source is untrusted. Our backend content is trusted.
                
                Why we removed DOMPurify:
                DOMPurify was sanitizing (filtering) the HTML to remove potentially dangerous
                elements, but it was also stripping out inline styles, certain HTML tags, and
                CSS classes that weren't in the ALLOWED_TAGS/ALLOWED_ATTR whitelist. This
                caused the blog posts to lose all their formatting, colors, and visual styling,
                appearing as plain text instead of the nicely formatted content we create.
                
                Since our blog content comes from our own trusted backend (not user input),
                we can safely render it without DOMPurify and preserve all the formatting.
            */}
            <div className="blog-content">
              <div
                className="blog-html-content"
                dangerouslySetInnerHTML={{
                  __html: currentBlog.html_content,
                }}
              />
            </div>

            {/* Social Sharing */}
            <div className="blog-sharing">
              <h3>Share this article</h3>
              <div className="sharing-buttons">
                <button
                  onClick={handleEmailShare}
                  className="share-button share-button-email"
                  title={!isMobile ? "Share via Email" : ""}
                >
                  <svg
                    className="share-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                  {/* <span className="share-label">Email</span> */}
                </button>
                <button
                  onClick={handleTextShare}
                  className="share-button share-button-text"
                  title={!isMobile ? "Share via Text Message" : ""}
                >
                  <svg
                    className="share-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z" />
                  </svg>
                  {/* <span className="share-label">Text</span> */}
                </button>
                <button
                  onClick={handleCopyLink}
                  className={`share-button share-button-copy ${
                    linkCopied ? "copied" : ""
                  }`}
                  title={
                    !isMobile ? (linkCopied ? "Link Copied!" : "Copy Link") : ""
                  }
                >
                  {linkCopied ? (
                    <svg
                      className="share-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                  ) : (
                    <svg
                      className="share-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                    </svg>
                  )}
                  {/* <span className="share-label">
                    {linkCopied ? "Copied!" : "Copy Link"}
                  </span> */}
                </button>
                <button
                  onClick={() => handleSocialShare("facebook")}
                  className="share-button share-button-facebook"
                  title={!isMobile ? "Share on Facebook" : ""}
                >
                  <svg
                    className="share-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  {/* <span className="share-label">Facebook</span> */}
                </button>
                <button
                  onClick={() => handleSocialShare("x")}
                  className="share-button share-button-x"
                  title={!isMobile ? "Share on X (Twitter)" : ""}
                >
                  <svg
                    className="share-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
                  </svg>
                  {/* <span className="share-label">X</span> */}
                </button>
                <button
                  onClick={() => handleSocialShare("linkedin")}
                  className="share-button share-button-linkedin"
                  title={!isMobile ? "Share on LinkedIn" : ""}
                >
                  <svg
                    className="share-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  {/* <span className="share-label">LinkedIn</span> */}
                </button>
              </div>
            </div>

            {/* Source URLs (if available) */}
            {(currentBlog.source_urls || currentBlog.source_url) && (
              <div className="blog-source">
                <p>
                  <strong>
                    Source
                    {currentBlog.source_urls &&
                    currentBlog.source_urls.length > 1
                      ? "s"
                      : ""}
                    :
                  </strong>
                </p>
                {currentBlog.source_urls &&
                currentBlog.source_urls.length > 0 ? (
                  // Display multiple sources
                  currentBlog.source_urls.length > 1 ? (
                    // Bullet format for multiple sources
                    <ul className="source-list">
                      {currentBlog.source_urls.map((url, index) => (
                        <li key={index}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    // Single source inline
                    <p>
                      <a
                        href={currentBlog.source_urls[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {currentBlog.source_urls[0]}
                      </a>
                    </p>
                  )
                ) : (
                  // Fallback to single source
                  <p>
                    <a
                      href={currentBlog.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {currentBlog.source_url}
                    </a>
                  </p>
                )}
              </div>
            )}

            {/* Back to Blogs */}
            <div className="blog-navigation">
              <button onClick={handleBackToHome} className="back-button">
                ← Back to Blog Section
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Blogs;
